import React, { useState } from 'react';
import BaseTableReport from 'components/atoms/base-table/report/report';
import { useTranslation } from 'react-i18next';
import { useSmsCampaignsData } from 'infrastructure/hooks/sms-campaigns/use-sms-campaigns-data';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useSendCampaigns } from 'infrastructure/hooks/sms-campaigns/use-sms-send-campaigns';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { prepareDoctorName } from 'infrastructure/functions';
import BaseButton from 'components/atoms/baseButton';
import { CampaignProgressStatus } from 'infrastructure/enums';
import { showResult } from 'infrastructure/hooks/utils';

import s from './styles.module.scss';
import { useColumns } from './columns';
import SmsCampaignsTableBulkSend from './bulk-send';
import AnswersDraggableModal from '../../answers/draggable-modal';
import CampaignsCsvUpload from '../csv-upload';

import type { SortParam } from 'infrastructure/types';
import type { TableProps } from 'antd';
import type {
  ISmsCampaign,
  ISmsCampaignsProgressFilters,
} from 'infrastructure/interfaces';

interface IProps {
  campaignGuid: string;
  filters: ISmsCampaignsProgressFilters;
  updateFilter: (data: ISmsCampaignsProgressFilters) => void;
}

const SmsCampaignsTable: React.FC<IProps> = ({
  filters,
  updateFilter,
  campaignGuid,
}) => {
  const { t } = useTranslation();

  const { Alert, showAlert } = useAlert();
  const [showBulk, setShowBulk] = useState(false);
  const [sendToAll, setSendToAll] = useState(false);
  const [opened, setOpened] = useState(false);
  const [openedUpload, setOpenedUpload] = useState(false);
  const [campaign, setCampaign] = useState<ISmsCampaign | undefined>();
  const [singleLoading, setSingleLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<ISmsCampaign[]>([]);

  const { data, isLoading, pageSize, refetch } = useSmsCampaignsData({
    ...filters,
    campaignGuid,
  });
  const { loading, sendCampaigns } = useSendCampaigns();

  const handleSend = async (rows: ISmsCampaign[], sendAll = false) => {
    setShowBulk(false);
    let res = true;
    if (rows.length > 1 || sendAll) {
      res = await showAlert({
        title: t('labels.bulkSmsSending'),
        messageTitle: sendAll
          ? t('messages.sendAllBulkSms')
          : t('messages.sendBulkSms', {
              count: selectedRows.length,
            }),
        successBtnLabel: t('labels.sendBulkSms'),
      });
    } else {
      setSingleLoading(true);
      setCampaign(rows[0]);
    }
    if (res) {
      await sendCampaigns({
        sendToAll: sendAll,
        campaignGuid,
        data: rows.map((el) => ({
          campaignProgressGuid: el.guid,
        })),
      })
        .then(() => {
          showResult();
        })
        .finally(() => {
          setSingleLoading(false);
          setCampaign(undefined);
          setSelectedRows([]);
          setSendToAll(false);
        });
    } else {
      setShowBulk(true);
    }
  };

  const handleClear = () => {
    setSendToAll(false);
    setSelectedRows([]);
  };

  const handleOpen = (c: ISmsCampaign) => {
    setCampaign(c);
    setOpened(true);
  };
  const handleClose = () => {
    setCampaign(undefined);
    setOpened(false);
  };

  const columns = useColumns({
    page: filters.page!,
    pageSize,
    uploadCampaign: campaign,
    sending: loading,
    onSend: handleSend,
    onOpen: handleOpen,
  });

  const handleChangePage = (page: number) => {
    updateFilter({
      ...filters,
      page,
    });
  };
  const handleSort = (sort?: SortParam) => {
    const state = sort && sort[1] ? sort[1] : undefined;
    updateFilter({
      ...filters,
      nameSort: state,
    });
  };

  const rowSelection: TableProps<ISmsCampaign>['rowSelection'] = {
    selectedRowKeys: selectedRows.map((el) => el.guid),
    onChange: (keys, rows) => {
      setSelectedRows(rows);
      setShowBulk(rows.length > 1);
    },
    getCheckboxProps(record) {
      if (record.status === CampaignProgressStatus.Completed) {
        return {
          disabled: true,
        };
      }
      return {};
    },
  };

  const handleSendToAll = () => {
    setShowBulk(true);
    setSendToAll(true);
    setSelectedRows(
      data.data.filter((el) => el.status !== CampaignProgressStatus.Completed),
    );
  };

  const handleAfterUpload = () => {
    setOpenedUpload(false);
    refetch();
  };

  const headerControls = (
    <>
      <BaseButton
        label={t('labels.addRecipients')}
        onClick={() => setOpenedUpload(true)}
      />
      <BaseButton
        type="secondary"
        label={t('labels.sendToAll')}
        onClick={handleSendToAll}
      />
    </>
  );

  return (
    <div>
      {showBulk && (
        <SmsCampaignsTableBulkSend
          sendToAll={sendToAll}
          length={selectedRows.length}
          onClear={handleClear}
          onSend={() => handleSend(selectedRows, sendToAll)}
        />
      )}
      <BaseSpinWrapper spinning={loading && !singleLoading}>
        <BaseTableReport
          className={s.table}
          rowKey="guid"
          headerTitle={t('labels.recipients')}
          headerControls={headerControls}
          isLoading={isLoading}
          columns={columns as any}
          rowSelection={rowSelection as any}
          dataSource={data?.data}
          totalCount={data?.count}
          pagination={{
            current: filters.page,
            pageSize,
            total: data?.count,
            onChange: handleChangePage,
          }}
          onSort={handleSort}
          showCount
          anchor
        />
      </BaseSpinWrapper>

      {campaign && opened && (
        <AnswersDraggableModal
          title={prepareDoctorName({
            firstName: campaign.patient.firstName,
            lastName: campaign.patient.lastName,
            title: campaign.patient.title,
          })}
          subTitle={t('smsCampaign.dragModalSubtitle')}
          outcomes={campaign.outcomes}
          patient={campaign.patient}
          onClose={handleClose}
          opened
          campaignGuid={campaignGuid}
        />
      )}
      <CampaignsCsvUpload
        opened={openedUpload}
        campaignGuid={campaignGuid}
        onClose={handleAfterUpload}
      />
      {Alert}
    </div>
  );
};

export default SmsCampaignsTable;
